import {
	TaskCTAType,
	TaskStatus,
	TaskStatusEnum,
	UpdateTaskDto,
} from "@dune/types";
import {
	Avatar,
	AvatarFallback,
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	FileIcon,
	FileTypeLabel,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	TextArea,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@dune/ui";
import { formatSimpleDate } from "@dune/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
	Calendar,
	CalendarX,
	CheckCircle2,
	CircleAlert,
	Link2Off,
	Loader2,
	RotateCcw,
	Save,
	Trash2,
	UserCircle,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { privateAxios } from "../../../../api/axios";
import { getDocumentUrl } from "../../../../api/queries/files.queries";
import {
	useDeleteTaskForProposition,
	useDetachDocumentFromTask,
	useGetAssignableUsers,
	useUpdateTaskForProposition,
} from "../../../../api/queries/tasks.queries";
import { useAttachDocumentToTask } from "../../../../api/mutations/tasks.mutations";
import { propositionsQueryKeys } from "../../../../api/query-keys/propositions.query-keys";
import ConfirmationDialog from "../../../../component/common/ConfirmationDialog";
import { usePanel } from "../../../../hooks/usePanel";
import { useDocumentStore } from "../../../../stores/document.store";
import { useTaskActions } from "../../../../stores/task.store";
import { TaskDetailsPanelProps } from "../../../../types/panel.types";
import { replacePlaceholders } from "../../../../utils/pathUtils";
import GenerateQuittancementModal from "../modals/GenerateQuittancementModal";
import SendDocumentModal from "../modals/SendDocumentModal";
import SignDocumentDuneModal from "../modals/SignDocumentDuneModal";
import GenerateDocumentModal from "../modals/GenerateDocumentModal";
import GenerateGFAPropositionDocumentsModal from "../modals/GenerateGFAPropositionDocumentsModal";
import TaskUploadDocumentModal from "../../../../component/common/TaskUploadDocumentModal";

interface TaskFormData {
	title?: string;
	description: string;
	assigneeId: number | null;
	status?: TaskStatus;
}

const getTaskStatusLabel = (status: string): string => {
	switch (status) {
		case "TO_DO":
			return "À faire";
		case "IN_PROGRESS":
			return "En cours";
		case "DONE":
			return "Terminé";
		default:
			return status;
	}
};

const getTaskStatusColorClasses = (status: string): string => {
	switch (status) {
		case "TO_DO":
			return "bg-gray-100 text-gray-800 border-gray-300";
		case "IN_PROGRESS":
			return "bg-blue-100 text-blue-800 border-blue-300";
		case "DONE":
			return "bg-green-100 text-green-800 border-green-300";
		default:
			return "bg-gray-100 text-gray-800 border-gray-300";
	}
};

const PropositionTaskDetailsPanel: React.FC<TaskDetailsPanelProps> = ({
	task,
	entityId,
}) => {
	const propositionId = entityId;
	const navigate = useNavigate();
	const { data: assignableUsers } = useGetAssignableUsers();
	const updateTaskMutation = useUpdateTaskForProposition(propositionId);
	const deleteTaskMutation = useDeleteTaskForProposition(propositionId);
	const detachDocumentMutation = useDetachDocumentFromTask();
	const attachDocumentMutation = useAttachDocumentToTask(propositionId);
	const { clearPanel } = usePanel();
	const { clearTaskSelection } = useTaskActions();
	const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
		useState(false);
	const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
		useState(false);
	const [isGeneratePropositionModalOpen, setIsGeneratePropositionModalOpen] =
		useState(false);
	const [isSendPropositionModalOpen, setIsSendPropositionModalOpen] =
		useState(false);
	const [isSignPropositionDuneModalOpen, setIsSignPropositionDuneModalOpen] =
		useState(false);
	const [
		isGenerateQuittancementModalOpen,
		setIsGenerateQuittancementModalOpen,
	] = useState(false);
	const [isGenerateContractModalOpen, setIsGenerateContractModalOpen] =
		useState(false);
	const [isSendContractModalOpen, setIsSendContractModalOpen] = useState(false);
	const [isSignContractDuneModalOpen, setIsSignContractDuneModalOpen] =
		useState(false);
	const [
		isGenerateFinalCertificateModalOpen,
		setIsGenerateFinalCertificateModalOpen,
	] = useState(false);
	const [
		isGenerateQuittancementReceptionModalOpen,
		setIsGenerateQuittancementReceptionModalOpen,
	] = useState(false);
	const [
		isSendEndOfWorkAmendmentModalOpen,
		setIsSendEndOfWorkAmendmentModalOpen,
	] = useState(false);
	const [
		isSignEndOfWorkAmendmentDuneModalOpen,
		setIsSignEndOfWorkAmendmentDuneModalOpen,
	] = useState(false);
	const [
		isGeneratePropositionGfaDocumentModalOpen,
		setIsGeneratePropositionGfaDocumentModalOpen,
	] = useState(false);
	const [isCtaLoading, setIsCtaLoading] = useState(false);
	const queryClient = useQueryClient();
	const selectDocument = useDocumentStore((state) => state.selectDocument);

	const form = useForm<TaskFormData>({
		defaultValues: {
			title: task.title,
			description: task.description || "",
			assigneeId: task.assigneeId,
			status: task.status,
		},
	});

	const {
		control,
		handleSubmit,
		reset,
		formState: { isDirty, isValid },
		setValue,
		watch,
	} = form;

	useEffect(() => {
		reset({
			title: task.title,
			description: task.description || "",
			assigneeId: task.assigneeId,
			status: task.status,
		});
	}, [task, reset]);

	const onSubmit = async (data: TaskFormData) => {
		const updateData: UpdateTaskDto = {};

		if (data.title !== task.title) {
			updateData.title = data.title;
		}

		if (data.description !== task.description) {
			updateData.description = data.description;
		}

		if (data.assigneeId !== task.assigneeId) {
			updateData.assigneeId = data.assigneeId;
		}

		if (data.status !== task.status) {
			updateData.status = data.status;
		}

		if (Object.keys(updateData).length > 0) {
			await updateTaskMutation.mutateAsync({
				taskId: task.id,
				updateData,
			});

			reset({
				title: data.title,
				description: data.description,
				assigneeId: data.assigneeId,
				status: data.status,
			});
		}
	};

	const handleReset = () => {
		reset();
	};

	const handleDeleteTask = () => {
		setIsDeleteConfirmationOpen(true);
	};

	const handleConfirmDelete = async () => {
		try {
			await deleteTaskMutation.mutateAsync(task.id);
			setIsDeleteConfirmationOpen(false);

			await queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.tasks(propositionId),
				refetchType: "active",
			});

			clearTaskSelection();
			clearPanel();

			toast.success("Tâche supprimée avec succès");
		} catch (error) {
			console.error("Error deleting task:", error);
			toast.error("Erreur lors de la suppression de la tâche");
		}
	};

	const handleCTAClick = async () => {
		if (!task.cta) return;

		switch (task.cta.actionType) {
			case TaskCTAType.REDIRECT:
				if (task.cta.navigationPath) {
					const path = replacePlaceholders(task.cta.navigationPath, {
						id: propositionId.toString(),
					});
					navigate(path);
				}
				break;

			case TaskCTAType.ENDPOINT:
				if (task.cta.endpoint) {
					setIsCtaLoading(true);
					try {
						const response = await privateAxios.post(task.cta.endpoint, {
							propositionId,
							taskId: task.id,
						});

						if (response.data?.message) {
							toast.success(response.data.message);
						}

						await queryClient.invalidateQueries({
							queryKey: propositionsQueryKeys.tasks(propositionId),
						});
						await queryClient.invalidateQueries({
							queryKey: ["task", task.id],
						});
					} catch (error) {
						console.error("Error calling CTA endpoint:", error);
						toast.error("Erreur lors de l'exécution de l'action");
					} finally {
						setIsCtaLoading(false);
					}
				}
				break;

			case TaskCTAType.MODAL:
				if (task.cta.modalKey === "generate-proposition") {
					setIsGeneratePropositionModalOpen(true);
				} else if (task.cta.modalKey === "send-proposition") {
					setIsSendPropositionModalOpen(true);
				} else if (task.cta.modalKey === "sign-proposition-dune") {
					setIsSignPropositionDuneModalOpen(true);
				} else if (task.cta.modalKey === "generate-receipt") {
					setIsGenerateQuittancementModalOpen(true);
				} else if (task.cta.modalKey === "generate-contract") {
					setIsGenerateContractModalOpen(true);
				} else if (task.cta.modalKey === "send-contract") {
					setIsSendContractModalOpen(true);
				} else if (task.cta.modalKey === "sign-contract-dune") {
					setIsSignContractDuneModalOpen(true);
				} else if (task.cta.modalKey === "send-end-of-work-amendment") {
					setIsSendEndOfWorkAmendmentModalOpen(true);
				} else if (task.cta.modalKey === "sign-end-of-work-amendment-dune") {
					setIsSignEndOfWorkAmendmentDuneModalOpen(true);
				} else if (task.cta.modalKey === "generate-receipt-reception") {
					setIsGenerateQuittancementReceptionModalOpen(true);
				} else if (task.cta.modalKey === "generate-final-certificate") {
					setIsGenerateFinalCertificateModalOpen(true);
				} else if (task.cta.modalKey === "generate-proposition-document") {
					setIsGeneratePropositionGfaDocumentModalOpen(true);
				} else {
					console.warn("Modal non supportée:", task.cta.modalKey);
				}
				break;

			default:
				console.warn("Type CTA non supporté:", task.cta.actionType);
		}
	};

	const handleDetachDocument = async (fileId: string) => {
		try {
			await detachDocumentMutation.mutateAsync({
				taskId: task.id,
				fileId,
			});

			// Invalidate proposition tasks to refresh the UI
			await queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.tasks(propositionId),
				refetchType: "active",
			});
		} catch (error) {
			console.error("Error detaching document:", error);
		}
	};

	const handleAttachDocument = async (fileId: string) => {
		await attachDocumentMutation.mutateAsync({
			taskId: task.id,
			fileId,
		});

		await queryClient.invalidateQueries({
			queryKey: propositionsQueryKeys.tasks(propositionId),
			refetchType: "active",
		});
	};

	const selectedAssignee = watch("assigneeId")
		? assignableUsers?.find((user) => user.id === watch("assigneeId"))
		: null;

	const statusOptions = [
		{ value: TaskStatusEnum.TO_DO, label: "À faire" },
		{ value: TaskStatusEnum.IN_PROGRESS, label: "En cours" },
		{ value: TaskStatusEnum.DONE, label: "Terminé" },
	];

	return (
		<>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					{task.isEditable ? (
						<Controller
							name="title"
							control={control}
							render={({ field }) => (
								<Input
									{...field}
									className="text-xl font-bold text-gray-900 border-0 p-0 shadow-none focus:ring-0 bg-transparent"
									placeholder="Titre de la tâche"
								/>
							)}
						/>
					) : (
						<h3 className="text-xl font-bold text-gray-900">{task.title}</h3>
					)}
					<Badge className={getTaskStatusColorClasses(task.status)}>
						{getTaskStatusLabel(task.status)}
					</Badge>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					{/* Informations */}
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
						{/* Due date */}
						<div>
							<span className="mb-1 block text-sm font-medium text-gray-500">
								Échéance
							</span>
							<div className="flex items-center gap-2 text-sm text-gray-700">
								{task.status === "DONE" ? (
									task.dueDate &&
									task.completedAt &&
									new Date(task.completedAt) > new Date(task.dueDate) ? (
										<>
											<CircleAlert className="h-4 w-4 text-orange-500 flex-shrink-0" />
											<span className="text-orange-600">
												{formatSimpleDate(task.dueDate)}
											</span>
										</>
									) : (
										<>
											<CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
											<span className="text-green-600">
												{task.dueDate ? formatSimpleDate(task.dueDate) : "-"}
											</span>
										</>
									)
								) : task.dueDate ? (
									new Date(task.dueDate) < new Date() ? (
										<>
											<CalendarX className="h-4 w-4 text-red-500 flex-shrink-0" />
											<span className="text-red-600">
												{formatSimpleDate(task.dueDate)}
											</span>
										</>
									) : (
										<>
											<Calendar className="h-4 w-4 text-blue-500 flex-shrink-0" />
											<span className="text-green-600">
												{formatSimpleDate(task.dueDate)}
											</span>
										</>
									)
								) : (
									<>
										<Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
										<span className="text-gray-500">-</span>
									</>
								)}
							</div>
						</div>

						{/* Start / End */}
						<div>
							<span className="mb-1 block text-sm font-medium text-gray-500">
								Début/Fin
							</span>
							<div className="space-y-1">
								<div className="flex items-center gap-2 text-sm text-gray-700">
									<Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
									<span className="text-xs text-gray-500">Début:</span>
									{formatSimpleDate(task.createdAt)}
								</div>
								{task.completedAt && (
									<div className="flex items-center gap-2 text-sm text-gray-700">
										<Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
										<span className="text-xs text-gray-500">Fin:</span>
										{formatSimpleDate(task.completedAt)}
									</div>
								)}
							</div>
						</div>

						{/* Assignee */}
						<div>
							<span className="mb-1 block text-sm font-medium text-gray-500">
								Assigné à
							</span>
							<div className="flex items-center gap-2">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<button
											type="button"
											className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
										>
											<Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-offset-2 transition-all">
												{selectedAssignee ? (
													<AvatarFallback className="text-sm bg-blue-500 text-white">
														{selectedAssignee.firstname.charAt(0)}
														{selectedAssignee.lastname.charAt(0)}
													</AvatarFallback>
												) : (
													<AvatarFallback className="text-sm bg-gray-100 border-2 border-dashed border-blue-400">
														<UserCircle className="h-5 w-5 text-blue-400" />
													</AvatarFallback>
												)}
											</Avatar>
										</button>
									</DropdownMenuTrigger>
									<DropdownMenuContent className="z-50" align="start">
										<DropdownMenuItem
											onClick={() =>
												setValue("assigneeId", null, { shouldDirty: true })
											}
										>
											<Avatar className="h-4 w-4 mr-2">
												<AvatarFallback className="text-xs bg-gray-100">
													<UserCircle className="h-3 w-3 text-gray-400" />
												</AvatarFallback>
											</Avatar>
											Non assigné
										</DropdownMenuItem>
										{assignableUsers?.map((user) => (
											<DropdownMenuItem
												key={user.id}
												onClick={() =>
													setValue("assigneeId", user.id, { shouldDirty: true })
												}
											>
												<Avatar className="h-4 w-4 mr-2">
													<AvatarFallback className="text-xs">
														{user.firstname.charAt(0)}
														{user.lastname.charAt(0)}
													</AvatarFallback>
												</Avatar>
												{user.firstname} {user.lastname}
												<span className="ml-2 text-xs text-gray-500">
													({user.role})
												</span>
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
								{selectedAssignee && (
									<span className="text-sm text-gray-700">
										{selectedAssignee.firstname} {selectedAssignee.lastname}
									</span>
								)}
							</div>
						</div>
					</div>

					{/* Status - Only for manual tasks */}
					{task.isEditable && (
						<div className="space-y-2">
							<span className="text-sm font-medium text-gray-500">Statut</span>
							<Controller
								name="status"
								control={control}
								render={({ field }) => (
									<Select
										value={field.value}
										onValueChange={(value) =>
											field.onChange(value as TaskStatus, {
												shouldDirty: true,
											})
										}
									>
										<SelectTrigger className="w-48">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{statusOptions.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</div>
					)}

					{/* Actions */}
					{task.cta && (
						<div className="space-y-2 flex flex-col items-start">
							<span className="text-sm font-medium text-gray-500">Actions</span>
							<Button
								type="button"
								onClick={handleCTAClick}
								disabled={isCtaLoading}
							>
								{isCtaLoading ? (
									<>
										<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										Traitement en cours...
									</>
								) : (
									task.cta.label
								)}
							</Button>
						</div>
					)}

					{/* Documents liés */}
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-gray-500">
								Documents liés
							</span>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => setIsUploadDocumentModalOpen(true)}
							>
								Ajouter un document
							</Button>
						</div>
						{task.files && task.files.length > 0 && (
							<div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
								<div className="space-y-2">
									{task.files.map((taskFile) => (
										<div
											key={taskFile.file.id}
											className="flex items-center gap-3 rounded-md bg-white p-2 shadow-sm"
										>
											<div className="flex-shrink-0">
												<FileIcon
													mimeType={taskFile.file.mimeType}
													className="h-5 w-5"
												/>
											</div>
											<div className="flex-1 min-w-0">
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<p className="text-sm font-medium text-gray-900 truncate cursor-help max-w-[220px]">
																{taskFile.file.name}
															</p>
														</TooltipTrigger>
														<TooltipContent>
															<p className="max-w-xs break-words">
																{taskFile.file.name}
															</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
												<FileTypeLabel
													mimeType={taskFile.file.mimeType}
													className="text-gray-500"
												/>
											</div>
											<div className="flex items-center gap-2">
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={async () => {
														try {
															const url = await getDocumentUrl({
																bucket: "private",
																key: taskFile.file.key,
															});

															if (url) {
																selectDocument(
																	url,
																	taskFile.file.id,
																	taskFile.file.mimeType,
																	taskFile.file.name,
																);
															} else {
																toast.error(
																	"Impossible de récupérer l'URL du document",
																);
															}
														} catch (error) {
															console.error(
																"TaskDetailsPanel - Error getting document URL:",
																error,
															);
															toast.error(
																"Erreur lors de l'ouverture du document",
															);
														}
													}}
												>
													Voir
												</Button>
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																type="button"
																variant="ghost"
																size="sm"
																onClick={() =>
																	handleDetachDocument(taskFile.file.id)
																}
																disabled={detachDocumentMutation.isPending}
															>
																{detachDocumentMutation.isPending ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<Link2Off className="h-4 w-4" />
																)}
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															<p>Dissocier le document de la tâche</p>
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Comment */}
					<div>
						<Controller
							name="description"
							control={control}
							render={({ field }) => (
								<TextArea
									{...field}
									label="Commentaire"
									placeholder="Rédiger un commentaire"
									className="min-h-[120px] resize-none align-top"
									style={{ textAlign: "left", verticalAlign: "top" }}
								/>
							)}
						/>
					</div>

					{/* Footer */}
					<div className="sticky bottom-0 pt-4 border-t bg-white">
						<div className="flex justify-between items-center">
							{/* Delete button - Only for manual tasks */}
							{task.isEditable && (
								<Button
									type="button"
									variant="destructive"
									onClick={handleDeleteTask}
									disabled={deleteTaskMutation.isPending}
									className="flex items-center gap-2"
								>
									{deleteTaskMutation.isPending ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											Suppression...
										</>
									) : (
										<>
											<Trash2 className="h-4 w-4" />
											Supprimer
										</>
									)}
								</Button>
							)}

							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									onClick={handleReset}
									disabled={!isDirty || updateTaskMutation.isPending}
								>
									<RotateCcw className="h-4 w-4 mr-2" />
									Reset
								</Button>
								<Button
									type="submit"
									disabled={
										!isDirty || !isValid || updateTaskMutation.isPending
									}
								>
									{updateTaskMutation.isPending ? (
										"Sauvegarde..."
									) : (
										<>
											<Save className="h-4 w-4 mr-2" />
											Sauvegarder
										</>
									)}
								</Button>
							</div>
						</div>
					</div>
				</form>
			</div>

			{/* Confirmation de suppression */}
			<ConfirmationDialog
				open={isDeleteConfirmationOpen}
				onOpenChange={setIsDeleteConfirmationOpen}
				title="Supprimer la tâche"
				description="Êtes-vous sûr de vouloir supprimer cette tâche ? Cette action est irréversible."
				confirmText="Supprimer"
				cancelText="Annuler"
				variant="danger"
				onConfirm={handleConfirmDelete}
				isLoading={deleteTaskMutation.isPending}
			/>

			{/* Modal de génération de proposition */}
			{/* Modaux */}
			{isGeneratePropositionModalOpen && (
				<GenerateDocumentModal
					type="PROPOSITION"
					open={isGeneratePropositionModalOpen}
					onOpenChange={setIsGeneratePropositionModalOpen}
					propositionId={propositionId}
				/>
			)}

			{isSendPropositionModalOpen && (
				<SendDocumentModal
					type={"PROPOSITION"}
					open={isSendPropositionModalOpen}
					onOpenChange={setIsSendPropositionModalOpen}
					propositionId={propositionId}
				/>
			)}

			{isSignPropositionDuneModalOpen && (
				<SignDocumentDuneModal
					type={"PROPOSITION"}
					open={isSignPropositionDuneModalOpen}
					onOpenChange={setIsSignPropositionDuneModalOpen}
					propositionId={propositionId}
				/>
			)}

			{isGenerateQuittancementModalOpen && (
				<GenerateQuittancementModal
					open={isGenerateQuittancementModalOpen}
					onOpenChange={setIsGenerateQuittancementModalOpen}
					propositionId={propositionId}
				/>
			)}

			{isGenerateContractModalOpen && (
				<GenerateDocumentModal
					type="CONTRACT"
					open={isGenerateContractModalOpen}
					onOpenChange={setIsGenerateContractModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isSendContractModalOpen && (
				<SendDocumentModal
					type="CONTRACT"
					open={isSendContractModalOpen}
					onOpenChange={setIsSendContractModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isSignContractDuneModalOpen && (
				<SignDocumentDuneModal
					type={"CONTRACT"}
					open={isSignContractDuneModalOpen}
					onOpenChange={setIsSignContractDuneModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isGenerateFinalCertificateModalOpen && (
				<GenerateDocumentModal
					type={["FINAL_CERTIFICATE", "END_OF_WORK_AMENDMENT"]}
					open={isGenerateFinalCertificateModalOpen}
					onOpenChange={setIsGenerateFinalCertificateModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isGenerateQuittancementReceptionModalOpen && (
				<GenerateQuittancementModal
					open={isGenerateQuittancementReceptionModalOpen}
					onOpenChange={setIsGenerateQuittancementReceptionModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isSendEndOfWorkAmendmentModalOpen && (
				<SendDocumentModal
					type="END_OF_WORK_AMENDMENT"
					open={isSendEndOfWorkAmendmentModalOpen}
					onOpenChange={setIsSendEndOfWorkAmendmentModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isSignEndOfWorkAmendmentDuneModalOpen && (
				<SignDocumentDuneModal
					type="END_OF_WORK_AMENDMENT"
					open={isSignEndOfWorkAmendmentDuneModalOpen}
					onOpenChange={setIsSignEndOfWorkAmendmentDuneModalOpen}
					propositionId={propositionId}
				/>
			)}
			{isGeneratePropositionGfaDocumentModalOpen && (
				<GenerateGFAPropositionDocumentsModal
					types={["PROPOSITION", "CONTRACT", "QUITTANCE"]}
					open={isGeneratePropositionGfaDocumentModalOpen}
					onOpenChange={setIsGeneratePropositionGfaDocumentModalOpen}
					propositionId={propositionId}
				/>
			)}

			{isUploadDocumentModalOpen && (
				<TaskUploadDocumentModal
					open={isUploadDocumentModalOpen}
					onOpenChange={setIsUploadDocumentModalOpen}
					onUpload={handleAttachDocument}
					isPending={attachDocumentMutation.isPending}
				/>
			)}
		</>
	);
};

export default PropositionTaskDetailsPanel;
